/** Presentation helpers for company Client import. No second canonical registry. */

import { STATUS_LABELS, TYPE_LABELS } from "./clientLabels";
import {
  DEAL_TYPE_LABELS,
  PROPERTY_TYPE_LABELS,
} from "../../companyProperties/lib/propertyLabels";

export const CLASSIFICATION_LABELS = {
  READY: "Готово",
  WARNING: "Готово з попередженням",
  BLOCKED: "Заблоковано — рядок не буде створено",
  ERROR: "Помилка — рядок не буде імпортовано",
};

export const OUTCOME_LABELS = {
  created: "Створено",
  blocked: "Заблоковано",
  existing_source: "Джерело вже існує — оновлення не було",
  failed: "Помилка рядка",
};

export const OPERATION_STATE_LABELS = {
  pending: "Перервано — операцію можна продовжити",
  running: "Перервано — операцію можна продовжити",
  completed: "Завершено",
  completed_with_errors: "Завершено з помилками",
  failed: "Збій операції",
};

export const DIAGNOSTIC_CATEGORY_LABELS = {
  parser: "Парсер",
  required_field: "Обов'язкове поле",
  enum: "Зіставлення значення",
  responsible_member: "Відповідальний",
  duplicate: "Дублікат",
  source_identity: "Ідентичність джерела",
  preference: "Побажання",
  client_field: "Поле клієнта",
  content_safety: "Правила публікації",
};

const TERMINAL_OPERATION_STATES = new Set([
  "completed",
  "completed_with_errors",
  "failed",
]);

export function classificationLabel(code) {
  return CLASSIFICATION_LABELS[code] || "Стан рядка";
}

export function outcomeLabel(code) {
  return OUTCOME_LABELS[code] || "Результат рядка";
}

export function operationStateLabel(state) {
  return OPERATION_STATE_LABELS[state] || "Стан операції";
}

export function isTerminalOperationState(state) {
  return TERMINAL_OPERATION_STATES.has(state);
}

export function isInterruptedOperation(state) {
  return state === "pending" || state === "running";
}

export const INSPECT_WARNING_LABELS = {
  formula_ignored: "Формули у файлі проігноровано і не виконано",
  additional_sheets_ignored: "Інші аркуші не переглянуто, доки аркуш не обрано явно",
  duplicate_header: "У файлі повторено заголовок колонки",
  empty_sheet: "Аркуш не містить рядків даних",
  too_many_sheets: "У файлі більше аркушів, ніж дозволяє сервер",
  unknown_sheet: "Обраний аркуш не знайдено у файлі",
};

export const DIAGNOSTIC_CODE_LABELS = {
  formula_ignored: "Формули проігноровано і не виконано",
  required_field: "Обов'язкове значення порожнє",
  missing_required_field: "Обов'язкове значення порожнє",
  unresolved_enum: "Значення не має канонічного зіставлення",
  unmapped_enum_value: "Значення не має канонічного зіставлення",
  invalid_enum: "Значення не є канонічним варіантом",
  invalid_client_type: "Тип клієнта не є канонічним варіантом",
  invalid_client_status: "Статус клієнта не є канонічним варіантом",
  invalid_client_field: "Поле клієнта некоректне",
  invalid_email: "Електронна адреса некоректна",
  invalid_external_id: "Зовнішній ідентифікатор некоректний",
  invalid_field_length: "Значення перевищує допустиму довжину",
  max_length: "Значення перевищує допустиму довжину",
  invalid_preference_enum: "Побажання не є канонічним варіантом",
  invalid_preference_range: "Діапазон побажання некоректний",
  invalid_preference_value: "Значення побажання некоректне",
  unresolved_responsible: "Відповідального не зіставлено з учасником компанії",
  invalid_responsible_user: "Відповідальний має бути активним учасником компанії",
  duplicate_phone_in_file: "Той самий телефон повторюється у файлі",
  duplicate_external_id_in_file: "Той самий зовнішній ідентифікатор повторюється у файлі",
  existing_company_phone: "У компанії вже є клієнт із таким номером телефону",
  existing_company_email_warning:
    "У компанії вже є клієнт із такою електронною адресою. Електронна адреса не є унікальним ідентифікатором клієнта.",
  existing_source_identity: "Джерело вже існує",
  source_namespace_required: "Потрібен простір джерела",
  content_policy_violation:
    "Текст порушує правила публікації. Змініть формулювання та спробуйте знову.",
};

export const API_ERROR_LABELS = {
  idempotency_conflict: "Конфлікт ключа ідемпотентності. Новий ключ автоматично не створюється.",
  company_import_forbidden: "Імпорт недоступний для цієї компанії.",
  invalid_idempotency_key: "Ключ ідемпотентності некоректний.",
  invalid_source_namespace: "Простір джерела некоректний.",
  invalid_mapping_shape: "Зіставлення має некоректну форму.",
  missing_required_mapping: "Не вистачає обов'язкового зіставлення.",
  unknown_source_column: "Колонку не знайдено у файлі.",
  duplicate_target: "Одну колонку зіставлено з кількома полями.",
};

const FIELD_KIND_LABELS = {
  text: "текст",
  enum: "значення",
  decimal: "число",
  integer: "ціле число",
  boolean: "так/ні",
  responsible: "відповідальний",
  preference: "побажання",
};

export function fieldKindLabel(kind) {
  return FIELD_KIND_LABELS[kind] || "";
}

export function classificationTone(code) {
  if (code === "READY") return "ready";
  if (code === "WARNING") return "warning";
  if (code) return "error";
  return "neutral";
}

export function outcomeTone(code) {
  if (code === "created") return "ready";
  if (code === "existing_source") return "warning";
  if (code === "blocked" || code === "failed") return "error";
  return "neutral";
}

export function operationStateTone(state) {
  if (state === "completed") return "ready";
  if (state === "completed_with_errors" || state === "pending" || state === "running") {
    return "warning";
  }
  if (state === "failed") return "error";
  return "neutral";
}

export function diagnosticCodeLabel(code) {
  return DIAGNOSTIC_CODE_LABELS[code] || "";
}

export function inspectWarningText(warning) {
  const code = warning && warning.code ? String(warning.code) : "";
  const known = INSPECT_WARNING_LABELS[code] || "";
  return { primary: known || "Попередження файлу" };
}

export function diagnosticText(item) {
  const code = item && item.code ? String(item.code) : "";
  const known = diagnosticCodeLabel(code);
  const category = diagnosticCategoryLabel(item && item.category);
  if (known) {
    return { primary: category ? `${category}. ${known}` : known };
  }
  return { primary: "Не вдалося обробити значення" };
}

export function diagnosticCodeText(code) {
  const known = diagnosticCodeLabel(code);
  return { primary: known || "Не вдалося обробити значення" };
}

export function presentApiError(view) {
  const code = view && view.code ? String(view.code) : "";
  const known = API_ERROR_LABELS[code] || "";
  return { primary: known || "Запит не виконано." };
}

export function textError(primary, secondary = "") {
  return { primary, secondary };
}

const CLIENT_FIELD_ENUM_LABELS = {
  type: TYPE_LABELS,
  status: STATUS_LABELS,
  "preference.deal_type": DEAL_TYPE_LABELS,
  "preference.property_type": PROPERTY_TYPE_LABELS,
};

export function canonicalChoiceText(value, fieldKey) {
  const token = value == null ? "" : String(value);
  if (!token) return "";
  const fieldMap = fieldKey ? CLIENT_FIELD_ENUM_LABELS[fieldKey] : null;
  if (fieldMap) return fieldMap[token] || "Канонічне значення";
  if (TYPE_LABELS[token]) return TYPE_LABELS[token];
  if (STATUS_LABELS[token]) return STATUS_LABELS[token];
  if (DEAL_TYPE_LABELS[token]) return DEAL_TYPE_LABELS[token];
  if (PROPERTY_TYPE_LABELS[token]) return PROPERTY_TYPE_LABELS[token];
  return "Канонічне значення";
}

export const IMPORT_FIELD_LABELS = {
  external_id: "Зовнішній ідентифікатор",
  first_name: "Ім'я",
  last_name: "Прізвище",
  phone: "Телефон",
  email: "Електронна адреса",
  type: "Тип клієнта",
  status: "Статус",
  source: "Джерело",
  notes: "Нотатки",
  responsible_user: "Відповідальний",
  "preference.deal_type": "Тип угоди",
  "preference.property_type": "Тип нерухомості",
  "preference.min_price": "Мінімальна ціна",
  "preference.max_price": "Максимальна ціна",
  "preference.min_area": "Мінімальна площа",
  "preference.max_area": "Максимальна площа",
  "preference.rooms_min": "Мінімум кімнат",
  "preference.rooms_max": "Максимум кімнат",
  "preference.city": "Місто",
  "preference.district": "Район",
};

function hasUkrainianCopy(value) {
  return /[А-Яа-яІіЇїЄєҐґ]/.test(String(value || ""));
}

export function importFieldLabel(field) {
  const key = field && field.key ? String(field.key) : "";
  if (key && IMPORT_FIELD_LABELS[key]) return IMPORT_FIELD_LABELS[key];
  const label = field && field.label ? String(field.label) : "";
  if (hasUkrainianCopy(label)) return label;
  return "Поле";
}

export function missingMappingText(fields, missingKeys) {
  const labels = (Array.isArray(missingKeys) ? missingKeys : []).map((key) => {
    const field = (Array.isArray(fields) ? fields : []).find((item) => item && item.key === key);
    return importFieldLabel(field || { key });
  });
  return `Потрібне зіставлення: ${labels.join(", ")}`;
}

export function formatLabel(format) {
  const value = format == null ? "" : String(format).toLowerCase();
  if (value === "csv") return "CSV";
  if (value === "xlsx") return "XLSX";
  return "Файл";
}

export function isZeroRowCompleted(operation) {
  const summary = operation && operation.summary;
  if (!operation || operation.state !== "completed" || !summary) return false;
  return (
    Number(summary.row_count) === 0 &&
    Number(summary.created) === 0 &&
    Number(summary.blocked) === 0 &&
    Number(summary.existing_source) === 0 &&
    Number(summary.failed) === 0
  );
}

export function diagnosticCategoryLabel(category) {
  return DIAGNOSTIC_CATEGORY_LABELS[category] || "";
}

export function committableCounts(summary) {
  const ready = Number(summary?.ready) || 0;
  const warning = Number(summary?.warning) || 0;
  const blocked = Number(summary?.blocked) || 0;
  const error = Number(summary?.error) || 0;
  return {
    attempted: ready + warning,
    blocked: blocked + error,
  };
}

export function missingRequiredMappings(fields, mapping) {
  return (Array.isArray(fields) ? fields : [])
    .filter((field) => field && field.required_mapping)
    .filter((field) => !mapping || !mapping[field.key])
    .map((field) => field.key);
}

export function sheetSelectionRequired(inspect) {
  const sheets = Array.isArray(inspect?.sheets) ? inspect.sheets : [];
  return inspect?.format === "xlsx" && sheets.length > 1;
}

export function sourceNamespacePrompt(mapping) {
  return Boolean(mapping && mapping.external_id);
}

export function sourceNamespaceMissing(mapping, sourceNamespace) {
  if (!sourceNamespacePrompt(mapping)) return false;
  return typeof sourceNamespace !== "string" || sourceNamespace.trim() === "";
}

export function requirementsClaimBlocked(requirements) {
  return Boolean(requirements && requirements.truncated);
}

export function unresolvedHeaders(headers, mapping) {
  const used = new Set(
    Object.values(mapping || {}).filter((value) => typeof value === "string" && value),
  );
  return (Array.isArray(headers) ? headers : []).filter((header) => !used.has(header));
}

export function explicitMappingFromSuggestions(suggestions) {
  const mapping = {};
  (Array.isArray(suggestions) ? suggestions : []).forEach((item) => {
    if (!item || item.status !== "suggested" || !item.suggestion || !item.header) {
      return;
    }
    if (mapping[item.suggestion]) return;
    mapping[item.suggestion] = item.header;
  });
  return mapping;
}

export function activeMemberOptions(payload) {
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.results)
      ? payload.results
      : [];
  return rows
    .filter((row) => row && row.status !== "suspended" && row.status !== "ended")
    .map((row) => {
      const user = row.user || null;
      const userId = user && user.id != null ? Number(user.id) : Number(row.user_id);
      return {
        userId,
        label: memberLabel(user, userId),
      };
    })
    .filter((row) => Number.isInteger(row.userId) && row.userId > 0);
}

function memberLabel(user, userId) {
  if (!user) return `Користувач #${userId}`;
  const fullname = typeof user.fullname === "string" ? user.fullname.trim() : "";
  if (fullname) return fullname;
  const handle = typeof user.handle === "string" ? user.handle.trim() : "";
  if (handle) return handle;
  return `Користувач #${userId}`;
}

function stableObject(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const sorted = {};
  Object.keys(value)
    .sort()
    .forEach((key) => {
      const item = value[key];
      if (item == null || item === "") return;
      sorted[key] = item;
    });
  return sorted;
}

function stableNested(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const sorted = {};
  Object.keys(value)
    .sort()
    .forEach((key) => {
      const child = stableObject(value[key]);
      if (Object.keys(child).length) sorted[key] = child;
    });
  return sorted;
}

export function importRequestFingerprint({
  fileToken,
  sheet,
  mapping,
  valueMappings,
  responsibleMapping,
  sourceNamespace,
}) {
  return JSON.stringify({
    fileToken: fileToken || "",
    sheet: sheet || "",
    mapping: stableObject(mapping),
    valueMappings: stableNested(valueMappings),
    responsibleMapping: stableObject(responsibleMapping),
    sourceNamespace: typeof sourceNamespace === "string" ? sourceNamespace : "",
  });
}

export function createIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `import-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function resolveCommitKey({ previous, fingerprint, createKey }) {
  if (
    previous &&
    previous.key &&
    previous.fingerprint === fingerprint
  ) {
    return previous;
  }
  const nextKey = typeof createKey === "function" ? createKey() : createIdempotencyKey();
  return { fingerprint, key: nextKey };
}

export function readOperationId(search) {
  const params = new URLSearchParams(
    typeof search === "string" ? search : "",
  );
  const raw = params.get("operation");
  if (!raw || !/^\d+$/.test(raw)) return null;
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 1) return null;
  return value;
}

export async function fileContentToken(file) {
  if (!file) return "";
  let bytes = new Uint8Array();
  if (typeof file.arrayBuffer === "function") {
    const buffer = await file.arrayBuffer();
    bytes = new Uint8Array(buffer);
  }
  if (!bytes.length && typeof file.text === "function") {
    const encoded = new TextEncoder().encode(await file.text());
    bytes = encoded;
  }
  let hash = 5381;
  for (let index = 0; index < bytes.length; index += 1) {
    hash = ((hash * 33) ^ bytes[index]) >>> 0;
  }
  return `${file.size}:${bytes.length}:${hash}`;
}

export function allowedValuesFor(fields, key) {
  const field = (Array.isArray(fields) ? fields : []).find((item) => item.key === key);
  if (!field || !Array.isArray(field.allowed_values)) return [];
  return field.allowed_values;
}
